<template>
  <tile v-if="loading">
    <loader />
  </tile>
  <tile v-bind="$attrs" class="cert-updates-tile" v-show="!loading">
    <div>
      <CertificationDropdown
        v-show="loaded && certificationCount > 1"
        :options="certificationDropdownOptions || []"
        :selectedId="certificationId"
        style="margin-bottom: 1rem"
        @update:certificationId="selectCertification($event)"
      />
      <CertificationUpdate
        :loaded="loaded"
        :certificationCount="certificationCount"
        :status="selectedCertification?.details?.status"
        :showLoader="loading || !loaded"
      >
        <template #description>
          {{ selectedCertification?.details?.description }}
        </template>
        <CertificationRequestDetails
          ref="certificationRequestDetailsRef"
          v-if="certificationRequestDetailsVisible"
          :enrollmentTerm="selectedCertification?.enrollmentTerm"
          :requestedAmount="selectedCertification?.requestedAmount"
          :schoolName="selectedCertification?.schoolName"
          :statusLastUpdated="selectedCertification?.createdAt"
        />
        <DisbursementSummary
          v-if="disbursementsCount > 0"
          :certification="selectedCertification"
          :disbursement="
            selectedCertification?.disbursements?.[selectedCertification?.disbursements?.length - 1]
          "
          :disbursementGroups="selectedCertification?.disbursementGroupsByStatus"
        />
        <CollapsibleSection
          v-if="disbursementsCount > 0"
          :inverted="showAllDisbursements === true"
          :isVisible="showAllDisbursements"
          @toggle="showAllDisbursements = !showAllDisbursements"
        >
          <template #toggle>
            <button
              type="button"
              class="link-styled-button text-underline"
              :aria-expanded="showAllDisbursements ? 'true' : 'false'"
              aria-controls="disbursements"
              @click.prevent="showAllDisbursements = !showAllDisbursements"
            >
              {{ showAllDisbursements ? "Hide" : "View" }} All
            </button>
          </template>
          <Disbursements
            v-show="showAllDisbursements"
            id="disbursements"
            :certification="selectedCertification"
            :disbursements="selectedCertification?.disbursements || []"
          />
        </CollapsibleSection>
      </CertificationUpdate>
    </div>
  </tile>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import Tile from "@/components/shared/Tile.vue";
import { useStore } from "vuex";
import Loader from "@/components/shared/Loader.vue";
import CollapsibleSection from "@/components/transitions/CollapsibleSection.vue";
import CertificationDropdown from "@/components/certification-updates/CertificationDropdown.vue";
import CertificationUpdate from "@/components/certification-updates/CertificationUpdate.vue";
import CertificationRequestDetails from "@/components/certification-updates/CertificationRequestDetails.vue";
import DisbursementSummary from "@/components/certification-updates/DisbursementSummary.vue";
import Disbursements from "@/components/certification-updates/Disbursements.vue";
import { CertificationStatusTypes } from "@/config/enums";
import { CertificationDropdownOption, CertificationModelVM } from "@/models/certificationService";

useStore();

const certificationRequestDetailsRef = ref<InstanceType<typeof CertificationRequestDetails> | null>(
  null,
);

const props = defineProps<{
  certifications?: CertificationModelVM[];
  loading: boolean;
}>();

const certificationId = ref<number>(0);
const selectedCertification = ref();

const showAllDisbursements = ref<boolean>(false);
const gettingCertifications = ref<boolean>(true);
const loaded = ref<boolean>(false);

const certificationRequestDetailsVisible = computed<boolean>(() => {
  // if status is REQUESTED
  const statuses = [
    Number(CertificationStatusTypes.CertificationRequestPending),
    Number(CertificationStatusTypes.ReadyForCertification),
		Number(CertificationStatusTypes.SentForCertification)
  ];

  if (certificationCount.value === 0) {
    return false;
  }

  return statuses.includes(selectedCertification.value?.details?.id);
});

const certificationCount = computed<number>(() => {
  if (props.certifications && props.certifications?.length > 0) {
    return props.certifications?.length;
  }
  return 0;
});

const disbursementsCount = computed<number>(() => {
  if (selectedCertification.value) {
    return (selectedCertification.value?.disbursements || [])?.length;
  }

  return 0;
});

const currentCertification = computed(() => {
  if (props.certifications && props.certifications?.length > 0 && certificationId.value > 0) {
    return props.certifications.find(c => c.id === certificationId.value);
  }

  return props.certifications && props.certifications?.length > 0
    ? props.certifications[0]
    : undefined;
});

const certificationDropdownOptions = computed<CertificationDropdownOption[]>(() => {
  if (props.certifications) {
    return props.certifications.map((c: CertificationModelVM) => {
      return {
        id: c.id,
        amount: c.amountDisplay || "",
        sequenceNumber: c.sequenceNumber || "",
        status: c.details?.status || "",
      };
    });
  }
  return [];
});
function selectCertification(id: number) {
  certificationId.value = Number(id);
  selectedCertification.value = props.certifications?.find(c => c.id === Number(id));

  certificationRequestDetailsRef.value?.toggleDetails(false);
}

onMounted(() => {
  const certId =
    props.certifications && props.certifications?.length > 0 ? props.certifications[0]?.id : 0;
  selectCertification(certId);
});

watch(
  () => props.certifications?.length,
  (currentValue, oldValue) => {
    console.log(`certifications count update: ${oldValue} to ${currentValue}`);

    if (currentValue && currentValue > 0) {
      const certId =
        props.certifications && props.certifications?.length > 0 ? props.certifications[0]?.id : 0;
      selectCertification(certId);
      loaded.value = true;
    }
  },
);
defineExpose({
  gettingCertifications,
});
</script>

<style scoped src="@/styles/certification-updates/base.css"></style>
